import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, Plus, DollarSign, Receipt, ArrowUpDown, CheckCircle, Clock, AlertCircle, Share2, Copy, Mail, ExternalLink, Trash2, Edit3, MapPin, Camera, Bed, Car, Percent, Lock } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast as sonnerToast } from 'sonner';
import Tesseract from 'tesseract.js';
import { useSubscription } from '@/hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

interface ExpenseGroup {
  id: string;
  name: string;
  description?: string;
  currency: string;
  created_by: string;
  created_at: string;
  member_count?: number;
}

interface GroupExpense {
  id: string;
  description: string;
  amount: number;
  currency: string;
  paid_by: string;
  category?: string;
  notes?: string;
  expense_date: string;
  payer_name?: string;
  participants?: Array<{ user_id: string; share_amount: number; display_name?: string }>;
  trip_id?: string;
  trip_name?: string;
  receipt_url?: string;
  split_scenario?: 'accommodation' | 'transportation' | 'standard';
  scenario_details?: {
    nights?: number;
    distance?: number;
    seats?: number;
    per_person?: boolean;
  };
}

interface Trip {
  id: string;
  title: string;
  destination: string;
  start_date: string;
  end_date: string;
}

interface Balance {
  user_id: string;
  display_name: string;
  balance: number;
  owes: Array<{ to_user: string; to_name: string; amount: number }>;
  owed: Array<{ from_user: string; from_name: string; amount: number }>;
}

const EXPENSE_CATEGORIES = [
  'Food & Dining',
  'Transportation',
  'Accommodation',
  'Entertainment',
  'Shopping',
  'Activities',
  'Other'
];

export function ExpenseSharing() {
  const { user } = useAuth();
  const { toast } = useToast();
  const { hasFeature } = useSubscription();
  const navigate = useNavigate();
  const canUseExpenseSharing = hasFeature('budget_tracking');
  
  const [groups, setGroups] = useState<ExpenseGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<ExpenseGroup | null>(null);
  const [expenses, setExpenses] = useState<GroupExpense[]>([]);
  const [balances, setBalances] = useState<Balance[]>([]);
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dialog states
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [showJoinGroup, setShowJoinGroup] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showRenameGroup, setShowRenameGroup] = useState(false);
  const [showDeleteGroup, setShowDeleteGroup] = useState(false);
  const [showTravelScenario, setShowTravelScenario] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [editingExpense, setEditingExpense] = useState<GroupExpense | null>(null);

  const showUpgradePrompt = () => {
    sonnerToast.info(
      <div className="space-y-2">
        <p className="font-medium">✨ Premium Feature</p>
        <p className="text-sm">Group expense sharing is available with Premium Economy or First Class Pack.</p>
        <Button 
          size="sm" 
          className="mt-2 w-full"
          onClick={() => navigate('/pricing')}
        >
          Upgrade Now
        </Button>
      </div>,
      { duration: 5000 }
    );
  };

  // Form states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupCurrency, setNewGroupCurrency] = useState('USD');
  const [joinGroupId, setJoinGroupId] = useState('');
  const [renameGroupName, setRenameGroupName] = useState('');
  const [renameGroupDescription, setRenameGroupDescription] = useState('');

  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: '',
    category: '',
    notes: '',
    participants: [] as string[],
    splitType: 'equal' as 'equal' | 'custom' | 'payer_only' | 'accommodation' | 'transportation',
    customSplits: [] as Array<{ user_id: string; amount: string; display_name?: string }>,
    trip_id: '',
    receipt_url: '',
    scenario_nights: '',
    scenario_distance: '',
    scenario_seats: '',
    scenario_per_person: false
  });

  // Group members state for split options
  const [groupMembers, setGroupMembers] = useState<Array<{ user_id: string; display_name: string }>>([]);

  useEffect(() => {
    if (user) {
      fetchGroups();
      fetchTrips();
    }
  }, [user]);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupData();
    }
  }, [selectedGroup]);

  const fetchTrips = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('trips')
        .select('id, title, destination, start_date, end_date')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('start_date', { ascending: false });

      if (error) throw error;
      setTrips(data || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('expense_groups')
        .select(`
          *,
          group_members!inner(user_id)
        `)
        .eq('group_members.user_id', user?.id);

      if (error) throw error;

      // Get member counts
      const groupsWithCounts = await Promise.all(
        data.map(async (group) => {
          const { count } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id);
          
          return { ...group, member_count: count || 0 };
        })
      );

      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast({
        title: "Error",
        description: "Failed to fetch expense groups",
        variant: "destructive",
      });
    }
  };

  const fetchGroupData = async () => {
    if (!selectedGroup) return;

    setLoading(true);
    try {
      // Fetch group members first for split options
      const { data: membersData, error: membersError } = await supabase
        .from('group_members')
        .select('user_id, display_name')
        .eq('group_id', selectedGroup.id);

      if (membersError) throw membersError;

      // Get profile names for members
      const membersWithProfiles = await Promise.all(
        membersData.map(async (member) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', member.user_id)
            .maybeSingle();

          return {
            user_id: member.user_id,
            display_name: member.display_name || profileData?.full_name || 'Unknown'
          };
        })
      );

      setGroupMembers(membersWithProfiles);

      // Fetch expenses
      const { data: expensesData, error: expensesError } = await supabase
        .from('group_expenses')
        .select('*')
        .eq('group_id', selectedGroup.id)
        .order('expense_date', { ascending: false });

      if (expensesError) throw expensesError;

      // Get payer names and participants separately
      const expensesWithDetails = await Promise.all(
        expensesData.map(async (expense) => {
          // Get payer name
          const { data: payerData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', expense.paid_by)
            .maybeSingle();

          // Get participants
          const { data: participantsData } = await supabase
            .from('expense_participants')
            .select('user_id, share_amount')
            .eq('expense_id', expense.id);

          const participantsWithNames = await Promise.all(
            (participantsData || []).map(async (p) => {
              const { data: profileData } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('user_id', p.user_id)
                .maybeSingle();

              return {
                user_id: p.user_id,
                share_amount: p.share_amount,
                display_name: profileData?.full_name || 'Unknown'
              };
            })
          );

          return {
            ...expense,
            payer_name: payerData?.full_name || 'Unknown',
            participants: participantsWithNames,
            split_scenario: (expense.split_scenario as 'accommodation' | 'transportation' | 'standard') || undefined,
            scenario_details: expense.scenario_details ? (expense.scenario_details as { nights?: number; distance?: number; seats?: number; per_person?: boolean }) : undefined
          };
        })
      );

      setExpenses(expensesWithDetails);

      // Calculate balances
      await calculateBalances();
    } catch (error) {
      console.error('Error fetching group data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch group data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateBalances = async () => {
    if (!selectedGroup) return;

    try {
      // Get all group members
      const { data: membersData, error: membersError } = await supabase
        .from('group_members')
        .select('user_id, display_name')
        .eq('group_id', selectedGroup.id);

      if (membersError) throw membersError;

      // Get profile names for members
      const membersWithProfiles = await Promise.all(
        membersData.map(async (member) => {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name')
            .eq('user_id', member.user_id)
            .maybeSingle();

          return {
            ...member,
            profile_name: profileData?.full_name
          };
        })
      );

      // Get all expenses and participants
      const { data: expensesData, error: expensesError } = await supabase
        .from('group_expenses')
        .select(`
          id,
          amount,
          paid_by,
          currency,
          expense_participants(user_id, share_amount)
        `)
        .eq('group_id', selectedGroup.id);

      if (expensesError) throw expensesError;

      // Calculate balances for each member
      const balanceMap = new Map<string, { balance: number; display_name: string }>();
      
      membersWithProfiles.forEach(member => {
        balanceMap.set(member.user_id, {
          balance: 0,
          display_name: member.display_name || member.profile_name || 'Unknown'
        });
      });

      // Calculate what each person paid vs what they owe
      expensesData.forEach(expense => {
        const totalShares = expense.expense_participants.reduce((sum, p) => sum + p.share_amount, 0);
        
        // Add what the payer paid
        const payerBalance = balanceMap.get(expense.paid_by);
        if (payerBalance) {
          balanceMap.set(expense.paid_by, {
            ...payerBalance,
            balance: payerBalance.balance + expense.amount
          });
        }

        // Subtract what each participant owes
        expense.expense_participants.forEach(participant => {
          const participantBalance = balanceMap.get(participant.user_id);
          if (participantBalance) {
            balanceMap.set(participant.user_id, {
              ...participantBalance,
              balance: participantBalance.balance - participant.share_amount
            });
          }
        });
      });

      // Convert to balance array with debt relationships
      const balances: Balance[] = Array.from(balanceMap.entries()).map(([userId, data]) => ({
        user_id: userId,
        display_name: data.display_name,
        balance: data.balance,
        owes: [],
        owed: []
      }));

      // Calculate who owes whom (simplified - in a real app you'd want to minimize transactions)
      balances.forEach(person => {
        if (person.balance < 0) {
          // This person owes money
          const amountOwed = Math.abs(person.balance);
          balances.forEach(creditor => {
            if (creditor.balance > 0 && creditor.user_id !== person.user_id) {
              const amountToSettle = Math.min(amountOwed, creditor.balance);
              if (amountToSettle > 0.01) { // Avoid tiny amounts
                person.owes.push({
                  to_user: creditor.user_id,
                  to_name: creditor.display_name,
                  amount: amountToSettle
                });
                creditor.owed.push({
                  from_user: person.user_id,
                  from_name: person.display_name,
                  amount: amountToSettle
                });
              }
            }
          });
        }
      });

      setBalances(balances);
    } catch (error) {
      console.error('Error calculating balances:', error);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim() || !user) {
      console.log('Create group validation failed:', { 
        groupName: newGroupName.trim(), 
        user: user,
        userId: user?.id 
      });
      toast({
        title: "Error",
        description: !user ? "Please log in to create a group" : "Please enter a group name",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    
    try {
      // Verify authentication
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (!session || !session.user) {
        toast({
          title: "Authentication Error",
          description: "Please log out and log back in to create a group",
          variant: "destructive",
        });
        return;
      }

      // Check if we can query as the current user
      const { data: profileTest, error: profileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      console.log('Profile test:', profileTest);
      console.log('Profile error:', profileError);

      const groupData = {
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || null,
        currency: newGroupCurrency,
        created_by: session.user.id
      };

      console.log('=== GROUP CREATION DATA ===');
      console.log('Group data to insert:', groupData);

      // Attempt to create the group
      const { data: createdGroup, error: groupError } = await supabase
        .from('expense_groups')
        .insert(groupData)
        .select()
        .single();

      console.log('=== GROUP CREATION RESULT ===');
      console.log('Created group:', createdGroup);
      console.log('Group creation error:', groupError);

      if (groupError) {
        // Log detailed error information
        console.error('Detailed group creation error:', {
          code: groupError.code,
          message: groupError.message,
          details: groupError.details,
          hint: groupError.hint
        });
        throw groupError;
      }

      // Get user's display name from profiles for member creation
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', session.user.id)
        .maybeSingle();

      const displayName = profileData?.full_name || user.email?.split('@')[0] || 'Unknown';

      // Add creator as member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: createdGroup.id,
          user_id: session.user.id,
          display_name: displayName
        });

      if (memberError) {
        console.error('Member addition error:', memberError);
        throw memberError;
      }

      toast({
        title: "Success",
        description: `Group "${newGroupName}" created successfully!`,
      });

      sonnerToast.success(`Group "${newGroupName}" created! You can now invite others using the group ID: ${createdGroup.id.slice(0, 8)}...`);

      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupCurrency('USD');
      setShowCreateGroup(false);
      fetchGroups();
      setSelectedGroup(createdGroup);
    } catch (error) {
      console.error('=== FULL ERROR DETAILS ===');
      console.error('Error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error constructor:', error?.constructor?.name);
      
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create group. Please check console for details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const joinGroup = async () => {
    if (!joinGroupId.trim() || !user) return;

    try {
      // Check if group exists
      const { data: groupData, error: groupError } = await supabase
        .from('expense_groups')
        .select('*')
        .eq('id', joinGroupId)
        .single();

      if (groupError) {
        console.error('Group fetch error:', groupError);
        throw new Error('Group not found. Please check the group ID.');
      }

      // Get user's display name from profiles
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .maybeSingle();

      const displayName = profileData?.full_name || user.email?.split('@')[0] || 'Unknown';

      // Add user as member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: joinGroupId,
          user_id: user.id,
          display_name: displayName
        });

      if (memberError) {
        if (memberError.code === '23505') { // Unique constraint violation
          toast({
            title: "Already a member",
            description: "You're already a member of this group",
            variant: "destructive",
          });
          return;
        }
        console.error('Member addition error:', memberError);
        throw memberError;
      }

      toast({
        title: "Success",
        description: `Joined group "${groupData.name}" successfully!`,
      });

      sonnerToast.success(`Welcome to "${groupData.name}"! You can now start sharing expenses.`);

      setJoinGroupId('');
      setShowJoinGroup(false);
      fetchGroups();
    } catch (error) {
      console.error('Error joining group:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to join group. Please check the group ID.",
        variant: "destructive",
      });
    }
  };

  const addExpense = async () => {
    if (!selectedGroup || !expenseForm.description || !expenseForm.amount || !user) return;

    try {
      const amount = parseFloat(expenseForm.amount);
      if (isNaN(amount) || amount <= 0) {
        toast({
          title: "Invalid amount",
          description: "Please enter a valid amount",
          variant: "destructive",
        });
        return;
      }

      let participants: Array<{ user_id: string; share_amount: number }> = [];

      // Handle different split types
      if (expenseForm.splitType === 'payer_only') {
        // Only the payer is responsible for the expense
        participants = [{ user_id: user.id, share_amount: amount }];
      } else if (expenseForm.splitType === 'accommodation' || expenseForm.splitType === 'transportation') {
        // Travel scenario splits
        participants = calculateTravelScenarioSplit();
      } else if (expenseForm.splitType === 'custom') {
        // Custom splits - validate that amounts add up
        const customSplits = expenseForm.customSplits.filter(split => 
          split.amount && parseFloat(split.amount) > 0
        );
        
        if (customSplits.length === 0) {
          toast({
            title: "Invalid split",
            description: "Please specify custom amounts",
            variant: "destructive",
          });
          return;
        }

        const totalCustomAmount = customSplits.reduce((sum, split) => sum + parseFloat(split.amount), 0);
        if (Math.abs(totalCustomAmount - amount) > 0.01) {
          toast({
            title: "Split amounts don't match",
            description: `Custom amounts (${totalCustomAmount.toFixed(2)}) must equal total expense (${amount.toFixed(2)})`,
            variant: "destructive",
          });
          return;
        }

        participants = customSplits.map(split => ({
          user_id: split.user_id,
          share_amount: parseFloat(split.amount)
        }));
      } else {
        // Equal split among selected participants or all members
        let participantIds = expenseForm.participants;
        if (participantIds.length === 0) {
          participantIds = groupMembers.map(m => m.user_id);
        }

        const shareAmount = amount / participantIds.length;
        participants = participantIds.map(userId => ({
          user_id: userId,
          share_amount: shareAmount
        }));
      }

      // Create expense
      const { data: expenseData, error: expenseError } = await supabase
        .from('group_expenses')
        .insert({
          group_id: selectedGroup.id,
          description: expenseForm.description,
          amount,
          currency: selectedGroup.currency,
          paid_by: user.id,
          category: expenseForm.category || null,
          notes: expenseForm.notes || null,
          trip_id: expenseForm.trip_id || null,
          receipt_url: expenseForm.receipt_url || null,
          split_scenario: expenseForm.splitType === 'accommodation' || expenseForm.splitType === 'transportation' 
            ? expenseForm.splitType 
            : 'standard',
          scenario_details: {
            nights: expenseForm.scenario_nights ? parseInt(expenseForm.scenario_nights) : undefined,
            seats: expenseForm.scenario_seats ? parseInt(expenseForm.scenario_seats) : undefined
          }
        })
        .select()
        .single();

      if (expenseError) throw expenseError;

      // Add participants
      const participantData = participants.map(p => ({
        expense_id: expenseData.id,
        user_id: p.user_id,
        share_amount: p.share_amount
      }));

      const { error: participantError } = await supabase
        .from('expense_participants')
        .insert(participantData);

      if (participantError) throw participantError;

      toast({
        title: "Success",
        description: "Expense added successfully!",
      });

      setExpenseForm({
        description: '',
        amount: '',
        category: '',
        notes: '',
        participants: [],
        splitType: 'equal',
        customSplits: [],
        trip_id: '',
        receipt_url: '',
        scenario_nights: '',
        scenario_distance: '',
        scenario_seats: '',
        scenario_per_person: false
      });
      setShowAddExpense(false);
      fetchGroupData();
    } catch (error) {
      console.error('Error adding expense:', error);
      toast({
        title: "Error",
        description: "Failed to add expense",
        variant: "destructive",
      });
    }
  };

  const settleDebt = async (fromUser: string, toUser: string, amount: number) => {
    if (!selectedGroup || !user) return;

    try {
      const { error } = await supabase
        .from('settlements')
        .insert({
          group_id: selectedGroup.id,
          from_user: fromUser,
          to_user: toUser,
          amount,
          currency: selectedGroup.currency,
          notes: 'Settled via app'
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Payment settled successfully!",
      });

      fetchGroupData();
    } catch (error) {
      console.error('Error settling debt:', error);
      toast({
        title: "Error",
        description: "Failed to settle payment",
        variant: "destructive",
      });
    }
  };

  const copyGroupId = () => {
    if (selectedGroup) {
      navigator.clipboard.writeText(selectedGroup.id);
      sonnerToast.success('Group ID copied to clipboard!');
    }
  };

  const shareGroupLink = () => {
    if (selectedGroup) {
      const shareUrl = `${window.location.origin}?join=${selectedGroup.id}`;
      navigator.clipboard.writeText(shareUrl);
      sonnerToast.success('Invitation link copied to clipboard!');
    }
  };

  const sendEmailInvite = () => {
    if (!selectedGroup || !inviteEmail.trim()) return;
    
    const subject = `Join my expense group: ${selectedGroup.name}`;
    const body = `Hi! I'd like to invite you to join my expense sharing group "${selectedGroup.name}".

You can join by:
1. Using this Group ID: ${selectedGroup.id}
2. Or clicking this link: ${window.location.origin}?join=${selectedGroup.id}

This will help us track and split expenses easily during our trip/activities.`;

    const mailtoUrl = `mailto:${inviteEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
    
    setInviteEmail('');
    setShowInviteDialog(false);
    sonnerToast.success('Email invitation opened in your default email app!');
  };

  const renameGroup = async () => {
    if (!selectedGroup || !renameGroupName.trim() || !user) return;

    if (selectedGroup.created_by !== user.id) {
      toast({
        title: "Permission denied",
        description: "Only the group creator can rename the group",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('expense_groups')
        .update({
          name: renameGroupName.trim(),
          description: renameGroupDescription.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedGroup.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Group renamed successfully!",
      });

      sonnerToast.success(`Group renamed to "${renameGroupName}"!`);

      setRenameGroupName('');
      setRenameGroupDescription('');
      setShowRenameGroup(false);
      fetchGroups();
      
      // Update selected group
      setSelectedGroup(prev => prev ? { ...prev, name: renameGroupName.trim(), description: renameGroupDescription.trim() || undefined } : null);
    } catch (error) {
      console.error('Error renaming group:', error);
      toast({
        title: "Error",
        description: "Failed to rename group",
        variant: "destructive",
      });
    }
  };

  const deleteGroup = async () => {
    if (!selectedGroup || !user) return;

    if (selectedGroup.created_by !== user.id) {
      toast({
        title: "Permission denied",
        description: "Only the group creator can delete the group",
        variant: "destructive",
      });
      return;
    }

    try {
      // Delete in order: participants, expenses, members, then group
      await supabase.from('expense_participants').delete().in('expense_id', 
        expenses.map(e => e.id)
      );
      
      await supabase.from('settlements').delete().eq('group_id', selectedGroup.id);
      await supabase.from('group_expenses').delete().eq('group_id', selectedGroup.id);
      await supabase.from('group_members').delete().eq('group_id', selectedGroup.id);
      
      const { error } = await supabase
        .from('expense_groups')
        .delete()
        .eq('id', selectedGroup.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Group deleted successfully!",
      });

      sonnerToast.success(`Group "${selectedGroup.name}" has been deleted.`);

      setShowDeleteGroup(false);
      setSelectedGroup(null);
      fetchGroups();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: "Error",
        description: "Failed to delete group",
        variant: "destructive",
      });
    }
  };

  const openEditExpense = (expense: GroupExpense) => {
    setEditingExpense(expense);
    setExpenseForm({
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category || '',
      notes: expense.notes || '',
      participants: expense.participants?.map(p => p.user_id) || [],
      splitType: 'equal', // Default to equal for editing
      customSplits: expense.participants?.map(p => ({
        user_id: p.user_id,
        amount: p.share_amount.toString(),
        display_name: p.display_name
      })) || [],
      trip_id: expense.trip_id || '',
      receipt_url: expense.receipt_url || '',
      scenario_nights: '',
      scenario_distance: '',
      scenario_seats: '',
      scenario_per_person: false
    });
    setShowEditExpense(true);
  };

  const updateExpense = async () => {
    if (!editingExpense || !selectedGroup || !expenseForm.description || !expenseForm.amount || !user) return;

    try {
      const amount = parseFloat(expenseForm.amount);
      if (isNaN(amount) || amount <= 0) {
        toast({
          title: "Invalid amount",
          description: "Please enter a valid amount",
          variant: "destructive",
        });
        return;
      }

      let participants: Array<{ user_id: string; share_amount: number }> = [];

      // Handle different split types
      if (expenseForm.splitType === 'payer_only') {
        participants = [{ user_id: editingExpense.paid_by, share_amount: amount }];
      } else if (expenseForm.splitType === 'custom') {
        const customSplits = expenseForm.customSplits.filter(split => 
          split.amount && parseFloat(split.amount) > 0
        );
        
        if (customSplits.length === 0) {
          toast({
            title: "Invalid split",
            description: "Please specify custom amounts",
            variant: "destructive",
          });
          return;
        }

        const totalCustomAmount = customSplits.reduce((sum, split) => sum + parseFloat(split.amount), 0);
        if (Math.abs(totalCustomAmount - amount) > 0.01) {
          toast({
            title: "Split amounts don't match",
            description: `Custom amounts must equal total expense`,
            variant: "destructive",
          });
          return;
        }

        participants = customSplits.map(split => ({
          user_id: split.user_id,
          share_amount: parseFloat(split.amount)
        }));
      } else {
        let participantIds = expenseForm.participants;
        if (participantIds.length === 0) {
          participantIds = groupMembers.map(m => m.user_id);
        }

        const shareAmount = amount / participantIds.length;
        participants = participantIds.map(userId => ({
          user_id: userId,
          share_amount: shareAmount
        }));
      }

      // Update expense
      const { error: expenseError } = await supabase
        .from('group_expenses')
        .update({
          description: expenseForm.description,
          amount,
          category: expenseForm.category || null,
          notes: expenseForm.notes || null
        })
        .eq('id', editingExpense.id);

      if (expenseError) throw expenseError;

      // Delete old participants and add new ones
      await supabase
        .from('expense_participants')
        .delete()
        .eq('expense_id', editingExpense.id);

      const participantData = participants.map(p => ({
        expense_id: editingExpense.id,
        user_id: p.user_id,
        share_amount: p.share_amount
      }));

      const { error: participantError } = await supabase
        .from('expense_participants')
        .insert(participantData);

      if (participantError) throw participantError;

      toast({
        title: "Success",
        description: "Expense updated successfully!",
      });

      setExpenseForm({
        description: '',
        amount: '',
        category: '',
        notes: '',
        participants: [],
        splitType: 'equal',
        customSplits: [],
        trip_id: '',
        receipt_url: '',
        scenario_nights: '',
        scenario_distance: '',
        scenario_seats: '',
        scenario_per_person: false
      });
      setEditingExpense(null);
      setShowEditExpense(false);
      fetchGroupData();
    } catch (error) {
      console.error('Error updating expense:', error);
      toast({
        title: "Error",
        description: "Failed to update expense",
        variant: "destructive",
      });
    }
  };

  const scanReceipt = async (file: File) => {
    if (!file) return;

    setScanningReceipt(true);
    try {
      const { data: { text } } = await Tesseract.recognize(file, 'eng');
      
      // Try to extract amount from OCR text
      const amountMatches = text.match(/\$?(\d+\.?\d*)/g);
      if (amountMatches && amountMatches.length > 0) {
        // Take the largest number as the total
        const amounts = amountMatches.map(m => parseFloat(m.replace('$', '')));
        const maxAmount = Math.max(...amounts);
        setExpenseForm(prev => ({ ...prev, amount: maxAmount.toString() }));
        sonnerToast.success('Receipt scanned! Amount extracted.');
      } else {
        sonnerToast.info('Receipt scanned but no amount found. Please enter manually.');
      }

      // Upload receipt image to storage
      const fileName = `receipts/${user?.id}/${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(fileName, file);

      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage
          .from('documents')
          .getPublicUrl(fileName);
        
        setExpenseForm(prev => ({ ...prev, receipt_url: publicUrl }));
      }
    } catch (error) {
      console.error('Error scanning receipt:', error);
      sonnerToast.error('Failed to scan receipt. Please enter details manually.');
    } finally {
      setScanningReceipt(false);
    }
  };

  const handleReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      scanReceipt(file);
    }
  };

  const calculateTravelScenarioSplit = (): Array<{ user_id: string; share_amount: number }> => {
    const amount = parseFloat(expenseForm.amount);
    const participants = expenseForm.participants.length > 0 
      ? expenseForm.participants 
      : groupMembers.map(m => m.user_id);

    if (expenseForm.splitType === 'accommodation') {
      // Accommodation: split by nights and persons
      const nights = parseInt(expenseForm.scenario_nights) || 1;
      const perNightPerPerson = amount / (nights * participants.length);
      return participants.map(userId => ({
        user_id: userId,
        share_amount: perNightPerPerson * nights
      }));
    } else if (expenseForm.splitType === 'transportation') {
      // Transportation: can be distance-based or seat-based
      if (expenseForm.scenario_seats) {
        const seats = parseInt(expenseForm.scenario_seats);
        const perSeat = amount / seats;
        return participants.map(userId => ({
          user_id: userId,
          share_amount: perSeat
        }));
      }
      // Default to equal split for transportation
      const perPerson = amount / participants.length;
      return participants.map(userId => ({
        user_id: userId,
        share_amount: perPerson
      }));
    }

    // Fallback to equal split
    const shareAmount = amount / participants.length;
    return participants.map(userId => ({
      user_id: userId,
      share_amount: shareAmount
    }));
  };

  const openRenameDialog = () => {
    if (selectedGroup) {
      setRenameGroupName(selectedGroup.name);
      setRenameGroupDescription(selectedGroup.description || '');
      setShowRenameGroup(true);
    }
  };

  if (!user) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Please sign in to use expense sharing</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-cyan-500/20 overflow-hidden">
      <div className="relative bg-gradient-to-br from-cyan-50 via-teal-50/50 to-transparent dark:from-cyan-950/30 dark:via-teal-950/20 dark:to-transparent border-b border-cyan-500/10">
        <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-gradient-to-br from-cyan-400/20 to-teal-500/20 blur-3xl pointer-events-none" aria-hidden="true" />
        <CardHeader className="relative">
          <CardTitle className="flex items-center gap-3 text-xl sm:text-2xl">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400 to-teal-500 text-white shadow-md shadow-cyan-500/30">
              <Users className="h-5 w-5" />
            </span>
            <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              Group Expense Sharing
            </span>
            {!canUseExpenseSharing && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Lock className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-medium">✨ Premium Feature</p>
                    <p className="text-sm">Available with Premium Economy or First Class Pack. Upgrade to split expenses with friends!</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </CardTitle>
          <CardDescription className="text-sm sm:text-base">
            Split expenses with friends and track who owes what
          </CardDescription>
        </CardHeader>
      </div>
      <CardContent className="space-y-6">
        {/* Group Selection */}
        <div className="flex flex-wrap gap-3">
          {groups.map((group) => (
            <Button
              key={group.id}
              variant={selectedGroup?.id === group.id ? "default" : "outline"}
              onClick={() => setSelectedGroup(group)}
              className="flex items-center gap-2"
            >
              <Users className="h-4 w-4" />
              {group.name}
              <Badge variant="secondary" className="text-xs">
                {group.member_count}
              </Badge>
            </Button>
          ))}
          
          <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={(e) => {
                  if (!canUseExpenseSharing) {
                    e.preventDefault();
                    showUpgradePrompt();
                  }
                }}
              >
                <Plus className="h-4 w-4" />
                Create Group
                {!canUseExpenseSharing && <Lock className="h-3.5 w-3.5 ml-1 text-muted-foreground" />}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Group</DialogTitle>
                <DialogDescription>
                  Create a new expense sharing group
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="groupName">Group Name</Label>
                  <Input
                    id="groupName"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="e.g., Trip to Paris"
                  />
                </div>
                <div>
                  <Label htmlFor="groupDescription">Description (Optional)</Label>
                  <Textarea
                    id="groupDescription"
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    placeholder="Group description..."
                  />
                </div>
                <div>
                  <Label htmlFor="groupCurrency">Currency</Label>
                  <Select value={newGroupCurrency} onValueChange={setNewGroupCurrency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="JPY">JPY - Japanese Yen</SelectItem>
                      <SelectItem value="GBP">GBP - Pound Sterling</SelectItem>
                      <SelectItem value="AUD">AUD - Australian Dollar</SelectItem>
                      <SelectItem value="CAD">CAD - Canadian Dollar</SelectItem>
                      <SelectItem value="CHF">CHF - Swiss Franc</SelectItem>
                      <SelectItem value="CNY">CNY - Chinese Yuan</SelectItem>
                      <SelectItem value="HKD">HKD - Hong Kong Dollar</SelectItem>
                      <SelectItem value="NZD">NZD - New Zealand Dollar</SelectItem>
                      <SelectItem value="KRW">KRW - South Korean Won</SelectItem>
                      <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                      <SelectItem value="SGD">SGD - Singapore Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={createGroup} className="w-full">
                  Create Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showJoinGroup} onOpenChange={setShowJoinGroup}>
            <DialogTrigger asChild>
              <Button 
                variant="outline" 
                className="flex items-center gap-2"
                onClick={(e) => {
                  if (!canUseExpenseSharing) {
                    e.preventDefault();
                    showUpgradePrompt();
                  }
                }}
              >
                <ArrowUpDown className="h-4 w-4" />
                Join Group
                {!canUseExpenseSharing && <Lock className="h-3.5 w-3.5 ml-1 text-muted-foreground" />}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Join Existing Group</DialogTitle>
                <DialogDescription>
                  Enter the group ID to join an existing group
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="groupId">Group ID</Label>
                  <Input
                    id="groupId"
                    value={joinGroupId}
                    onChange={(e) => setJoinGroupId(e.target.value)}
                    placeholder="Group ID"
                  />
                </div>
                <Button onClick={joinGroup} className="w-full">
                  Join Group
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {selectedGroup && (
          <div className="space-y-6">
            {/* Group Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{selectedGroup.name}</h3>
                  {selectedGroup.description && (
                    <p className="text-sm text-muted-foreground">{selectedGroup.description}</p>
                  )}
                </div>
                <div className="text-right">
                  <Badge variant="outline">{selectedGroup.currency}</Badge>
                  <p className="text-xs text-muted-foreground mt-1">
                    ID: {selectedGroup.id.slice(0, 8)}...
                  </p>
                </div>
              </div>
              
              {/* Invite Section */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">Group Actions</h4>
                  <div className="flex items-center gap-2">
                    {selectedGroup.created_by === user?.id && (
                      <>
                        <Dialog open={showRenameGroup} onOpenChange={setShowRenameGroup}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="flex items-center gap-2">
                              <Edit3 className="h-3 w-3" />
                              Rename
                            </Button>
                          </DialogTrigger>
                        </Dialog>
                        
                        <Dialog open={showDeleteGroup} onOpenChange={setShowDeleteGroup}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="flex items-center gap-2 text-destructive hover:text-destructive">
                              <Trash2 className="h-3 w-3" />
                              Delete
                            </Button>
                          </DialogTrigger>
                        </Dialog>
                      </>
                    )}
                    
                    <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" variant="outline" className="flex items-center gap-2">
                          <Share2 className="h-3 w-3" />
                          Invite
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Invite to {selectedGroup.name}</DialogTitle>
                          <DialogDescription>
                            Share the group details to invite others to join
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="p-4 bg-muted rounded-lg space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium">Group ID</p>
                                <p className="text-xs text-muted-foreground font-mono">{selectedGroup.id}</p>
                              </div>
                              <Button size="sm" variant="outline" onClick={copyGroupId}>
                                <Copy className="h-3 w-3 mr-1" />
                                Copy
                              </Button>
                            </div>
                            <Button variant="outline" onClick={shareGroupLink} className="w-full">
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Copy Invitation Link
                            </Button>
                          </div>
                          
                          <div className="space-y-3">
                            <Label htmlFor="inviteEmail">Send Email Invitation</Label>
                            <div className="flex gap-2">
                              <Input
                                id="inviteEmail"
                                type="email"
                                placeholder="friend@example.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                              />
                              <Button onClick={sendEmailInvite} disabled={!inviteEmail.trim()}>
                                <Mail className="h-4 w-4 mr-1" />
                                Send
                              </Button>
                            </div>
                          </div>
                          
                          <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                              Share the Group ID or invitation link with friends so they can join instantly and start tracking expenses together.
                            </AlertDescription>
                          </Alert>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Invite others to join your group, or manage group settings if you're the creator
                </p>
              </div>
            </div>

            {/* Rename Group Dialog */}
            <Dialog open={showRenameGroup} onOpenChange={setShowRenameGroup}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Rename Group</DialogTitle>
                  <DialogDescription>
                    Update the group name and description
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="renameGroupName">Group Name</Label>
                    <Input
                      id="renameGroupName"
                      value={renameGroupName}
                      onChange={(e) => setRenameGroupName(e.target.value)}
                      placeholder="Group name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="renameGroupDescription">Description (Optional)</Label>
                    <Textarea
                      id="renameGroupDescription"
                      value={renameGroupDescription}
                      onChange={(e) => setRenameGroupDescription(e.target.value)}
                      placeholder="Group description..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowRenameGroup(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={renameGroup} 
                      className="flex-1"
                      disabled={!renameGroupName.trim()}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Delete Group Dialog */}
            <Dialog open={showDeleteGroup} onOpenChange={setShowDeleteGroup}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Group</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete "{selectedGroup.name}"? This action cannot be undone and will remove all expenses and settlements.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Warning:</strong> This will permanently delete the group and all associated data including expenses, settlements, and member information.
                    </AlertDescription>
                  </Alert>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowDeleteGroup(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive"
                      onClick={deleteGroup} 
                      className="flex-1"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Group
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Edit Expense Dialog */}
            <Dialog open={showEditExpense} onOpenChange={setShowEditExpense}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit Expense</DialogTitle>
                  <DialogDescription>
                    Update the expense details and split
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="editExpenseDescription">Description</Label>
                    <Input
                      id="editExpenseDescription"
                      value={expenseForm.description}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="e.g., Dinner at restaurant"
                    />
                  </div>
                  <div>
                    <Label htmlFor="editExpenseAmount">Amount ({selectedGroup?.currency})</Label>
                    <Input
                      id="editExpenseAmount"
                      type="number"
                      step="0.01"
                      value={expenseForm.amount}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="editExpenseCategory">Category</Label>
                    <Select 
                      value={expenseForm.category} 
                      onValueChange={(value) => setExpenseForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {EXPENSE_CATEGORIES.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="editSplitType">How should this be split?</Label>
                    <Select 
                      value={expenseForm.splitType} 
                      onValueChange={(value: 'equal' | 'custom' | 'payer_only') => {
                        setExpenseForm(prev => ({ 
                          ...prev, 
                          splitType: value,
                          participants: value === 'equal' ? 
                            (prev.participants.length > 0 ? prev.participants : groupMembers.map(m => m.user_id)) : [],
                          customSplits: value === 'custom' ? 
                            (prev.customSplits.length > 0 ? prev.customSplits : groupMembers.map(m => ({
                              user_id: m.user_id,
                              amount: '',
                              display_name: m.display_name
                            }))) : []
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select split type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equal">Split equally</SelectItem>
                        <SelectItem value="custom">Custom amounts</SelectItem>
                        <SelectItem value="payer_only">Paid for themselves only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {expenseForm.splitType === 'equal' && (
                    <div className="space-y-3">
                      <Label>Split with which members?</Label>
                      <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                        {groupMembers.map((member) => (
                          <div key={member.user_id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`edit-member-${member.user_id}`}
                              checked={expenseForm.participants.includes(member.user_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setExpenseForm(prev => ({
                                    ...prev,
                                    participants: [...prev.participants, member.user_id]
                                  }));
                                } else {
                                  setExpenseForm(prev => ({
                                    ...prev,
                                    participants: prev.participants.filter(id => id !== member.user_id)
                                  }));
                                }
                              }}
                              className="rounded border-border"
                            />
                            <label 
                              htmlFor={`edit-member-${member.user_id}`}
                              className="text-sm cursor-pointer"
                            >
                              {member.display_name}
                              {member.user_id === user?.id && ' (me)'}
                            </label>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 text-xs">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setExpenseForm(prev => ({
                            ...prev,
                            participants: groupMembers.map(m => m.user_id)
                          }))}
                        >
                          Select All
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setExpenseForm(prev => ({
                            ...prev,
                            participants: []
                          }))}
                        >
                          Clear All
                        </Button>
                      </div>
                      {expenseForm.participants.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Split between {expenseForm.participants.length} member(s): {
                            expenseForm.amount ? 
                            `${(parseFloat(expenseForm.amount) / expenseForm.participants.length).toFixed(2)} ${selectedGroup?.currency} each` :
                            'Amount per person will be calculated'
                          }
                        </p>
                      )}
                    </div>
                  )}

                  {expenseForm.splitType === 'custom' && (
                    <div className="space-y-3">
                      <Label>Custom Split Amounts</Label>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {expenseForm.customSplits.map((split, index) => (
                          <div key={split.user_id} className="flex items-center gap-2">
                            <div className="flex items-center space-x-2 flex-1">
                              <input
                                type="checkbox"
                                checked={split.amount !== '' && parseFloat(split.amount) > 0}
                                onChange={(e) => {
                                  const newSplits = [...expenseForm.customSplits];
                                  if (e.target.checked) {
                                    newSplits[index].amount = expenseForm.amount ? 
                                      (parseFloat(expenseForm.amount) / groupMembers.length).toFixed(2) : '';
                                  } else {
                                    newSplits[index].amount = '';
                                  }
                                  setExpenseForm(prev => ({ ...prev, customSplits: newSplits }));
                                }}
                                className="rounded border-border"
                              />
                              <span className="text-sm w-20 truncate">
                                {split.display_name}
                                {split.user_id === user?.id && ' (me)'}
                              </span>
                            </div>
                            <Input
                              type="number"
                              step="0.01"
                              value={split.amount}
                              onChange={(e) => {
                                const newSplits = [...expenseForm.customSplits];
                                newSplits[index].amount = e.target.value;
                                setExpenseForm(prev => ({ ...prev, customSplits: newSplits }));
                              }}
                              placeholder="0.00"
                              className="w-24"
                              disabled={split.amount === '' && parseFloat(split.amount || '0') === 0}
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Total: {expenseForm.customSplits.reduce((sum, split) => 
                          sum + (parseFloat(split.amount) || 0), 0
                        ).toFixed(2)} {selectedGroup?.currency}
                        {expenseForm.amount && ` / ${expenseForm.amount} ${selectedGroup?.currency}`}
                      </p>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="editExpenseNotes">Notes (Optional)</Label>
                    <Textarea
                      id="editExpenseNotes"
                      value={expenseForm.notes}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Additional notes..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowEditExpense(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button onClick={updateExpense} className="flex-1">
                      Update Expense
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Tabs defaultValue="expenses" className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="expenses">Expenses</TabsTrigger>
                <TabsTrigger value="balances">Balances</TabsTrigger>
                <TabsTrigger value="activity">Activity</TabsTrigger>
              </TabsList>

              <TabsContent value="expenses" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Recent Expenses</h4>
                  <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        Add Expense
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Expense</DialogTitle>
                        <DialogDescription>
                          Add an expense to split with the group
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="expenseDescription">Description</Label>
                          <Input
                            id="expenseDescription"
                            value={expenseForm.description}
                            onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="e.g., Dinner at restaurant"
                          />
                        </div>
                        <div>
                          <Label htmlFor="expenseAmount">Amount ({selectedGroup.currency})</Label>
                          <Input
                            id="expenseAmount"
                            type="number"
                            step="0.01"
                            value={expenseForm.amount}
                            onChange={(e) => setExpenseForm(prev => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <Label htmlFor="expenseCategory">Category</Label>
                          <Select 
                            value={expenseForm.category} 
                            onValueChange={(value) => setExpenseForm(prev => ({ ...prev, category: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {EXPENSE_CATEGORIES.map((category) => (
                                <SelectItem key={category} value={category}>
                                  {category}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Trip Context Integration */}
                        {trips.length > 0 && (
                          <div>
                            <Label htmlFor="expenseTrip" className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              Link to Trip (Optional)
                            </Label>
                            <Select 
                              value={expenseForm.trip_id} 
                              onValueChange={(value) => setExpenseForm(prev => ({ ...prev, trip_id: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select trip" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="">None</SelectItem>
                                {trips.map((trip) => (
                                  <SelectItem key={trip.id} value={trip.id}>
                                    {trip.title} - {trip.destination}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}

                        {/* Receipt Scanner */}
                        <div>
                          <Label className="flex items-center gap-2">
                            <Camera className="h-4 w-4" />
                            Scan Receipt (Optional)
                          </Label>
                          <div className="flex gap-2">
                            <Input
                              type="file"
                              accept="image/*"
                              ref={fileInputRef}
                              onChange={handleReceiptUpload}
                              className="hidden"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={scanningReceipt}
                            >
                              {scanningReceipt ? 'Scanning...' : 'Upload Receipt'}
                            </Button>
                            {expenseForm.receipt_url && (
                              <Badge variant="secondary">Receipt attached</Badge>
                            )}
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="splitType">How should this be split?</Label>
                          <Select 
                            value={expenseForm.splitType} 
                            onValueChange={(value: 'equal' | 'custom' | 'payer_only' | 'accommodation' | 'transportation') => {
                              setExpenseForm(prev => ({ 
                                ...prev, 
                                splitType: value,
                                participants: value === 'equal' ? groupMembers.map(m => m.user_id) : [],
                                customSplits: value === 'custom' ? groupMembers.map(m => ({
                                  user_id: m.user_id,
                                  amount: '',
                                  display_name: m.display_name
                                })) : []
                              }));
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select split type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="equal">Split equally</SelectItem>
                              <SelectItem value="custom">Custom amounts</SelectItem>
                              <SelectItem value="payer_only">I paid for myself only</SelectItem>
                              <SelectItem value="accommodation">
                                <span className="flex items-center gap-2">
                                  <Bed className="h-4 w-4" />
                                  Accommodation Split
                                </span>
                              </SelectItem>
                              <SelectItem value="transportation">
                                <span className="flex items-center gap-2">
                                  <Car className="h-4 w-4" />
                                  Transportation Split
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Accommodation Scenario */}
                        {expenseForm.splitType === 'accommodation' && (
                          <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              Split accommodation costs fairly based on number of nights
                            </p>
                            <div>
                              <Label htmlFor="scenario_nights">Number of Nights</Label>
                              <Input
                                id="scenario_nights"
                                type="number"
                                min="1"
                                value={expenseForm.scenario_nights}
                                onChange={(e) => setExpenseForm(prev => ({ ...prev, scenario_nights: e.target.value }))}
                                placeholder="e.g., 3"
                              />
                            </div>
                          </div>
                        )}

                        {/* Transportation Scenario */}
                        {expenseForm.splitType === 'transportation' && (
                          <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              Split transportation costs based on seats or equal split
                            </p>
                            <div>
                              <Label htmlFor="scenario_seats">Number of Seats (Optional)</Label>
                              <Input
                                id="scenario_seats"
                                type="number"
                                min="1"
                                value={expenseForm.scenario_seats}
                                onChange={(e) => setExpenseForm(prev => ({ ...prev, scenario_seats: e.target.value }))}
                                placeholder="Leave empty for equal split"
                              />
                            </div>
                          </div>
                        )}

                        {expenseForm.splitType === 'equal' && (
                          <div className="space-y-3">
                            <Label>Split with which members?</Label>
                            <div className="grid grid-cols-1 gap-2 max-h-32 overflow-y-auto">
                              {groupMembers.map((member) => (
                                <div key={member.user_id} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`member-${member.user_id}`}
                                    checked={expenseForm.participants.includes(member.user_id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setExpenseForm(prev => ({
                                          ...prev,
                                          participants: [...prev.participants, member.user_id]
                                        }));
                                      } else {
                                        setExpenseForm(prev => ({
                                          ...prev,
                                          participants: prev.participants.filter(id => id !== member.user_id)
                                        }));
                                      }
                                    }}
                                    className="rounded border-border"
                                  />
                                  <label 
                                    htmlFor={`member-${member.user_id}`}
                                    className="text-sm cursor-pointer"
                                  >
                                    {member.display_name}
                                    {member.user_id === user?.id && ' (me)'}
                                  </label>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2 text-xs">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setExpenseForm(prev => ({
                                  ...prev,
                                  participants: groupMembers.map(m => m.user_id)
                                }))}
                              >
                                Select All
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setExpenseForm(prev => ({
                                  ...prev,
                                  participants: []
                                }))}
                              >
                                Clear All
                              </Button>
                            </div>
                            {expenseForm.participants.length > 0 && (
                              <p className="text-xs text-muted-foreground">
                                Split between {expenseForm.participants.length} member(s): {
                                  expenseForm.amount ? 
                                  `${(parseFloat(expenseForm.amount) / expenseForm.participants.length).toFixed(2)} ${selectedGroup.currency} each` :
                                  'Amount per person will be calculated'
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {expenseForm.splitType === 'custom' && (
                          <div className="space-y-3">
                            <Label>Custom Split Amounts</Label>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {expenseForm.customSplits.map((split, index) => (
                                <div key={split.user_id} className="flex items-center gap-2">
                                  <div className="flex items-center space-x-2 flex-1">
                                    <input
                                      type="checkbox"
                                      checked={split.amount !== '' && parseFloat(split.amount) > 0}
                                      onChange={(e) => {
                                        const newSplits = [...expenseForm.customSplits];
                                        if (e.target.checked) {
                                          newSplits[index].amount = expenseForm.amount ? 
                                            (parseFloat(expenseForm.amount) / groupMembers.length).toFixed(2) : '';
                                        } else {
                                          newSplits[index].amount = '';
                                        }
                                        setExpenseForm(prev => ({ ...prev, customSplits: newSplits }));
                                      }}
                                      className="rounded border-border"
                                    />
                                    <span className="text-sm w-20 truncate">
                                      {split.display_name}
                                      {split.user_id === user?.id && ' (me)'}
                                    </span>
                                  </div>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={split.amount}
                                    onChange={(e) => {
                                      const newSplits = [...expenseForm.customSplits];
                                      newSplits[index].amount = e.target.value;
                                      setExpenseForm(prev => ({ ...prev, customSplits: newSplits }));
                                    }}
                                    placeholder="0.00"
                                    className="w-24"
                                    disabled={split.amount === '' && parseFloat(split.amount || '0') === 0}
                                  />
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Total: {expenseForm.customSplits.reduce((sum, split) => 
                                sum + (parseFloat(split.amount) || 0), 0
                              ).toFixed(2)} {selectedGroup.currency}
                              {expenseForm.amount && ` / ${expenseForm.amount} ${selectedGroup.currency}`}
                            </p>
                          </div>
                        )}

                        <div>
                          <Label htmlFor="expenseNotes">Notes (Optional)</Label>
                          <Textarea
                            id="expenseNotes"
                            value={expenseForm.notes}
                            onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                            placeholder="Additional notes..."
                          />
                        </div>
                        <Button onClick={addExpense} className="w-full">
                          Add Expense
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-3">
                  {expenses.length === 0 ? (
                    <div className="text-center p-6 text-muted-foreground">
                      <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No expenses yet. Add your first expense!</p>
                    </div>
                  ) : (
                    expenses.map((expense) => (
                      <div key={expense.id} className="p-4 border rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium">{expense.description}</h5>
                            <p className="text-sm text-muted-foreground">
                              Paid by {expense.payer_name} on {new Date(expense.expense_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="font-semibold">
                                {new Intl.NumberFormat('en-US', {
                                  style: 'currency',
                                  currency: expense.currency,
                                }).format(expense.amount)}
                              </p>
                              {expense.category && (
                                <Badge variant="secondary" className="text-xs">
                                  {expense.category}
                                </Badge>
                              )}
                            </div>
                            {(expense.paid_by === user?.id || selectedGroup?.created_by === user?.id) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditExpense(expense)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit3 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {expense.notes && (
                          <p className="text-sm text-muted-foreground">{expense.notes}</p>
                        )}
                        <div className="text-xs text-muted-foreground">
                          Split between: {expense.participants?.map(p => p.display_name).join(', ')}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="balances" className="space-y-4">
                <h4 className="font-medium">Group Balances</h4>
                <div className="space-y-3">
                  {balances.length === 0 ? (
                    <div className="text-center p-6 text-muted-foreground">
                      <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No balances to show. Add some expenses first!</p>
                    </div>
                  ) : (
                    balances.map((balance) => (
                      <div key={balance.user_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium">{balance.display_name}</h5>
                          <div className={`font-semibold ${
                            balance.balance > 0 ? 'text-green-600' : 
                            balance.balance < 0 ? 'text-red-600' : 'text-muted-foreground'
                          }`}>
                            {balance.balance > 0 ? '+' : ''}
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: selectedGroup.currency,
                            }).format(balance.balance)}
                          </div>
                        </div>
                        
                        {balance.owes.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-red-600">Owes:</p>
                            {balance.owes.map((debt, index) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span>→ {debt.to_name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">
                                    {new Intl.NumberFormat('en-US', {
                                      style: 'currency',
                                      currency: selectedGroup.currency,
                                    }).format(debt.amount)}
                                  </span>
                                  {balance.user_id === user?.id && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => settleDebt(balance.user_id, debt.to_user, debt.amount)}
                                      className="h-6 px-2 text-xs"
                                    >
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Settle
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {balance.owed.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <p className="text-sm font-medium text-green-600">Owed by:</p>
                            {balance.owed.map((credit, index) => (
                              <div key={index} className="flex items-center justify-between text-sm">
                                <span>← {credit.from_name}</span>
                                <span className="font-medium">
                                  {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: selectedGroup.currency,
                                  }).format(credit.amount)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="activity" className="space-y-4">
                <h4 className="font-medium">Recent Activity</h4>
                <div className="text-center p-6 text-muted-foreground">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>Activity feed coming soon!</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
}